import React from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {SlideInLeft, SlideOutRight} from 'react-native-reanimated';
import {observer} from 'mobx-react-lite';

import CartButton from '../../../../components/CartButton';
import Text from '../../../../components/ui-controls/Text';
import NoImage from '../../../../images/LMG_IMAGE_NOT_AVAILABLE.jpg';
import {AppRoute} from '../../../../models/AppRoute';
import useAppNavigation from '../../../../navigation/useAppNavigation';
import {useStores} from '../../../../stores/use-stores';
import theme from '../../../../style/theme';
import {toAUD} from '../../../../utils/functions';
import {makeByLineText} from '../../../../utils/products';

type CartLineItemProps = {
  item: any;
  index: any;
  disabled?: boolean;
  isTotalVisible?: any;
  isGiftCardStore?: boolean;
};

const CartLineItem = observer(
  ({
    item,
    index,
    disabled = false,
    isTotalVisible = true,
    isGiftCardStore = false,
  }: CartLineItemProps) => {
    const navigation = useAppNavigation();
    const {cartStore} = useStores();

    if (!item) return null;

    const onPress = () =>
      navigation.navigate(AppRoute.PRODUCT, {product: item});

    const byline = makeByLineText(item);

    const originalPrice =
      item.sell_price_cents !== item.normal_price_cents ? (
        <Text style={styles.originalPrice}>
          was {toAUD(item.normal_price_cents)}
        </Text>
      ) : null;

    return (
      <Animated.View
        testID="cart-line-item"
        entering={SlideInLeft.delay(index * 100)}
        exiting={SlideOutRight}>
        <TouchableOpacity
          onPress={() => onPress()}
          style={styles.item}
          disabled={disabled}>
          <View style={styles.itemInside}>
            <View style={styles.leftCol}>
              {!!item.links.image && (
                <Image
                  source={{uri: item.links.image}}
                  style={styles.tileImage}
                  resizeMode="contain"
                />
              )}
              {!item.links.image && (
                <Image
                  source={NoImage}
                  style={styles.tileImage}
                  resizeMode="contain"
                />
              )}
            </View>

            <View style={styles.rightCol}>
              <Text style={styles.itemText}>{item.name}</Text>
              <View style={{flexDirection: 'row', flex: 1}}>
                <View style={styles.origCol}>
                  <Text style={styles.itemQty}>{byline}</Text>
                  <View style={styles.priceLine}>
                    <Text style={styles.price}>
                      {toAUD(item.sell_price_cents)}
                    </Text>
                    <Text style={styles.priceUnit}> ea</Text>
                  </View>
                  {originalPrice}
                </View>

                <View style={styles.adjustCol}>
                  {!!isTotalVisible && (
                    <View style={styles.totalLine}>
                      <Text style={styles.totalText}>Total: </Text>
                      {!!cartStore.dirtyOrder && (
                        <View style={styles.activityHolder}>
                          <ActivityIndicator
                            style={styles.activityIndicator}
                            size="small"
                            color={theme.colour.activityIndicator.primary}
                          />
                        </View>
                      )}
                      {!!(!cartStore.dirtyOrder && item.subtotal) && (
                        <Text style={styles.totalPrice}>
                          {toAUD(item.subtotal.cents_inc_tax)}
                        </Text>
                      )}
                    </View>
                  )}
                  {!isGiftCardStore && (
                    <View style={styles.basketButtons}>
                      <CartButton item={item} />
                    </View>
                  )}
                </View>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  },
);

export default CartLineItem;

const styles = StyleSheet.create({
  leftCol: {
    flex: 1,
    marginVertical: 1,
    justifyContent: 'center',
  },
  tileImage: {
    ...Platform.select({
      web: {
        width: '95%',
        aspectRatio: 1,
        marginVertical: 4,
      },
      native: {
        flex: 1,
        width: null,
        height: null,
        resizeMode: 'contain',
      },
    }),
  },
  item: {
    borderWidth: 0.5,
    borderColor: 'grey',
    borderRadius: 4,
    paddingLeft: 4,
    backgroundColor: 'white',
    marginBottom: 8,
  },
  itemInside: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  origCol: {
    flex: 4.5,
    height: '100%',
    paddingVertical: 4,
    justifyContent: 'flex-start',
  },
  adjustCol: {
    flex: 5.5,
    height: '100%',
    paddingLeft: 4,
    paddingVertical: 4,
    justifyContent: 'space-between',
  },
  rightCol: {
    flex: 3,
    flexDirection: 'column',
    height: '100%',
    paddingVertical: 4,
    paddingHorizontal: 4,
    alignItems: 'center',
  },
  totalLine: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 22,
  },
  totalText: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    fontSize: 14,
    paddingTop: 2,
  },
  totalPrice: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    fontSize: 16,
    flex: 1,
    textAlign: 'right',
  },
  activityHolder: {
    flex: 1,
  },
  activityIndicator: {},
  itemText: {
    color: theme.colour.text.heading.primary,
    fontSize: 14,
    fontWeight: 'bold',
    alignSelf: 'flex-start',
  },
  itemQty: {
    color: 'grey',
    fontSize: 14,
  },
  priceLine: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  originalPrice: {
    color: 'grey',
    fontSize: 12,
  },
  price: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
  },
  priceUnit: {
    color: theme.colour.text.heading.primary,
  },
  basketButtons: {
    alignItems: 'flex-end',
    marginRight: 8,
    paddingBottom: 2,
    marginTop: 8,
  },
});
